/**
 * Redux slice des backlinks d'une note (chantier C6, « Référencé par »).
 *
 * Porte les données de `GET /api/notes/{noteId}/backlinks` pour la note
 * ouverte dans `ModalNote` et le drapeau de l'onglet du dock — sur le modèle de
 * `noteHistorySlice`. Garde anti-course : un résultat qui n'appartient plus à
 * la note courante (navigation pendant le chargement) est ignoré.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { noteService } from '../../services/noteAPI';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { LOADING_STATES } from '../../constants';
import type { LoadingState } from '../../types/redux';
import type {
    NoteBacklinkItem,
    NoteBacklinksData,
    NoteBacklinksTotals,
    NoteReferencingItem,
    NoteRelatedByEntreesItem,
    NoteSuiteItem,
} from '../../types/api';

export interface NoteBacklinksState {
    /** Note dont les données ci-dessous parlent (null = rien chargé). */
    noteId: string | null;
    referencedBy: NoteReferencingItem[];
    relatedByEntrees: NoteRelatedByEntreesItem[];
    suiviePar: NoteSuiteItem[];
    sansConceptCommun: NoteBacklinkItem[];
    totals: NoteBacklinksTotals;
    status: LoadingState;
    error: string | null;
    /** Onglet « Référencé par » du dock ouvert. */
    isPanelOpen: boolean;
}

const EMPTY_TOTALS: NoteBacklinksTotals = { referencedBy: 0, relatedByEntrees: 0 };
const EMPTY_REFERENCING: NoteReferencingItem[] = [];
const EMPTY_RELATED: NoteRelatedByEntreesItem[] = [];
const EMPTY_SUIVIE_PAR: NoteSuiteItem[] = [];
const EMPTY_SANS_CONCEPT: NoteBacklinkItem[] = [];

export const initialNoteBacklinksState: NoteBacklinksState = {
    noteId: null,
    referencedBy: [],
    relatedByEntrees: [],
    suiviePar: [],
    sansConceptCommun: [],
    totals: EMPTY_TOTALS,
    status: LOADING_STATES.IDLE,
    error: null,
    isPanelOpen: false,
};

export const fetchNoteBacklinks = createAsyncThunk<NoteBacklinksData, { noteId: string; instance: any }>(
    'noteBacklinks/fetch',
    async ({ noteId, instance }) => {
        const accessToken = await getTokenRedirect(tokenRequest, instance);
        if (!accessToken) {
            throw new Error('Impossible d\'obtenir le token d\'accès');
        }
        return noteService.getBacklinks(noteId, accessToken);
    }
);

const noteBacklinksSlice = createSlice({
    name: 'noteBacklinks',
    initialState: initialNoteBacklinksState,
    reducers: {
        openBacklinksPanel(state) {
            state.isPanelOpen = true;
        },
        closeBacklinksPanel(state) {
            state.isPanelOpen = false;
        },
        /** Fermeture de la modale : rien ne doit survivre à la note fermée. */
        resetBacklinks() {
            return initialNoteBacklinksState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNoteBacklinks.pending, (state, action) => {
                const { noteId } = action.meta.arg;
                if (state.noteId !== noteId) {
                    // Nouvelle note : ne pas montrer les liens de la précédente pendant le chargement.
                    state.referencedBy = [];
                    state.relatedByEntrees = [];
                    state.suiviePar = [];
                    state.sansConceptCommun = [];
                    state.totals = EMPTY_TOTALS;
                }
                state.noteId = noteId;
                state.status = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(fetchNoteBacklinks.fulfilled, (state, action) => {
                if (action.meta.arg.noteId !== state.noteId) return;
                state.referencedBy = action.payload?.referencedBy ?? [];
                state.relatedByEntrees = action.payload?.relatedByEntrees ?? [];
                state.suiviePar = action.payload?.suiviePar ?? [];
                state.sansConceptCommun = action.payload?.sansConceptCommun ?? [];
                state.totals = action.payload?.totals ?? EMPTY_TOTALS;
                state.status = LOADING_STATES.SUCCEEDED;
                state.error = null;
            })
            .addCase(fetchNoteBacklinks.rejected, (state, action) => {
                if (action.meta.arg.noteId !== state.noteId) return;
                state.status = LOADING_STATES.FAILED;
                state.error = action.error?.message || 'Impossible de charger les notes liées';
            });
    },
});

export const { openBacklinksPanel, closeBacklinksPanel, resetBacklinks } = noteBacklinksSlice.actions;

// Sélecteurs nul-sûrs : le store des tests de ModalNote peut ne pas porter ce slice.
const sliceOf = (state: any): NoteBacklinksState | undefined => state?.noteBacklinks;

export const selectIsBacklinksPanelOpen = (state: any): boolean => sliceOf(state)?.isPanelOpen ?? false;
export const selectBacklinksNoteId = (state: any): string | null => sliceOf(state)?.noteId ?? null;
export const selectBacklinksStatus = (state: any): LoadingState => sliceOf(state)?.status ?? LOADING_STATES.IDLE;
export const selectBacklinksError = (state: any): string | null => sliceOf(state)?.error ?? null;
export const selectReferencedBy = (state: any): NoteReferencingItem[] => sliceOf(state)?.referencedBy ?? EMPTY_REFERENCING;
export const selectRelatedByEntrees = (state: any): NoteRelatedByEntreesItem[] => sliceOf(state)?.relatedByEntrees ?? EMPTY_RELATED;
/** Les notes qui font suite à la note chargée (C22 SP2 bis). */
export const selectBacklinksSuiviePar = (state: any): NoteSuiteItem[] => sliceOf(state)?.suiviePar ?? EMPTY_SUIVIE_PAR;
/** Le voisinage direct de la note chargée, relié sans concept commun (C22 SP3). */
export const selectBacklinksSansConceptCommun = (state: any): NoteBacklinkItem[] => sliceOf(state)?.sansConceptCommun ?? EMPTY_SANS_CONCEPT;
export const selectBacklinksTotals = (state: any): NoteBacklinksTotals => sliceOf(state)?.totals ?? EMPTY_TOTALS;

/** Nombre de référents de `noteId` (0 si les données chargées parlent d'une autre note). */
export const selectReferencedByCount = (state: any, noteId: string | null | undefined): number => {
    const s = sliceOf(state);
    if (!s || !noteId || s.noteId !== noteId) return 0;
    return s.totals.referencedBy;
};

export default noteBacklinksSlice.reducer;
